import { useState } from 'react';
import { Alert, Button, Flex, Form, Input, Result, theme } from 'antd';
import { Link, useSearchParams } from 'react-router';
import { MENSAGEM_DE_FALHA_INESPERADA, TituloDaTela } from '@titularis/ui';
import { authClient } from '../../auth';
import { esquecerTokenDeRedefinicao, lerTokenDeRedefinicao } from './tokenDeRedefinicao';

interface Campos {
  password: string;
  confirmacao: string;
}

export function NovaSenhaPage() {
  const { token } = theme.useToken();
  const [params] = useSearchParams();
  /*
   * Lido uma vez e guardado no estado: depois do sucesso o token é esquecido,
   * e relê-lo a cada render trocaria "Senha alterada" por "Link inválido". O
   * endereço fica como reserva para quando o `sessionStorage` não existe.
   */
  const [codigo] = useState(() => lerTokenDeRedefinicao() ?? params.get('token'));
  const [enviando, setEnviando] = useState(false);
  const [pronto, setPronto] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const redefinir = async ({ password }: Campos) => {
    if (!codigo) return;

    setErro(null);
    setEnviando(true);

    try {
      const { error } = await authClient.resetPassword({
        newPassword: password,
        token: codigo,
      });

      if (error) {
        setErro('Este link expirou ou já foi usado. Peça um novo e-mail de redefinição.');
        return;
      }

      esquecerTokenDeRedefinicao();
      setPronto(true);
    } catch {
      setErro(MENSAGEM_DE_FALHA_INESPERADA);
    } finally {
      setEnviando(false);
    }
  };

  if (!codigo) {
    return (
      <Result
        style={{ padding: 0 }}
        status="warning"
        title="Link inválido"
        subTitle="Abra o link exatamente como recebeu no e-mail."
        extra={
          <Link to="/esqueci-a-senha">
            <Button type="primary" size="large" block>
              Pedir novo link
            </Button>
          </Link>
        }
      />
    );
  }

  if (pronto) {
    return (
      <Result
        style={{ padding: 0 }}
        status="success"
        title="Senha alterada"
        subTitle="Você já pode entrar com a nova senha."
        extra={
          <Link to="/entrar">
            <Button type="primary" size="large" block>
              Entrar
            </Button>
          </Link>
        }
      />
    );
  }

  return (
    <Flex vertical gap={token.margin}>
      <TituloDaTela
        titulo="Criar nova senha"
        descricao="Escolha a senha que você vai usar para entrar daqui em diante."
      />

      {erro ? <Alert type="error" showIcon title={erro} /> : null}

      <Form<Campos> layout="vertical" onFinish={redefinir} requiredMark={false} disabled={enviando}>
        <Form.Item
          name="password"
          label="Nova senha"
          rules={[
            { required: true, message: 'Escolha uma senha.' },
            { min: 8, message: 'Use pelo menos 8 caracteres.' },
          ]}
        >
          <Input.Password autoComplete="new-password" size="large" />
        </Form.Item>

        <Form.Item
          name="confirmacao"
          label="Repita a senha"
          dependencies={['password']}
          rules={[
            { required: true, message: 'Repita a senha.' },
            ({ getFieldValue }) => ({
              validator: (_rule, valor: string) =>
                !valor || getFieldValue('password') === valor
                  ? Promise.resolve()
                  : Promise.reject(new Error('As senhas não coincidem.')),
            }),
          ]}
        >
          <Input.Password autoComplete="new-password" size="large" />
        </Form.Item>

        <Button type="primary" htmlType="submit" size="large" block loading={enviando}>
          Salvar nova senha
        </Button>
      </Form>
    </Flex>
  );
}
